import { AntragFinishDto } from '@eBeihilfe/einreichung-shared/dto/antrag-finish.dto';
import { AntragMetaData, LegacyAntragsMetadata } from '@eBeihilfe/einreichung-shared/model';
import { AntragsFlag, AntragsType, FirstBelegMandatoryConfiguration, Guid } from '@ebeihilfe/core';
import { computed, signal } from '@angular/core';

import { Beleg } from './Beleg';
import { Seite } from './Seite';

export class Antrag {
  private readonly _antragsMetadata: LegacyAntragsMetadata[] | AntragMetaData[];
  private readonly _belege = signal<Beleg[]>([]);

  public doNotFinishMe = false;
  public readonly firstBelegMandatoryConfig: FirstBelegMandatoryConfiguration;
  public hasAcceptedDataProtection!: boolean;

  public hasSeiten = computed(() => this._belege().some((beleg) => beleg.seiten.length > 0));

  /** id of antrag, can only be set by backend */
  private _antragsId!: Guid;
  private _antragsType: AntragsType;
  private _isCompleted = false;
  /** time of creation, can only be set by backend */
  private _timestamp!: string;
  private _containerId!: string;
  private _isPristine = true;

  constructor(firstBelegMandatoryConfig?: FirstBelegMandatoryConfiguration) {
    this.firstBelegMandatoryConfig = firstBelegMandatoryConfig ?? {
      isActive: false,
      mandatoryPages: 0,
    };
    this._antragsMetadata = [];
    this._antragsType = AntragsType.UNDEFINED;
  }

  public get hasPendingData(): boolean {
    return !this.isComplete && this.hasSeiten();
  }

  public get antragsId(): Guid {
    return this._antragsId;
  }

  public get antragsType(): AntragsType {
    return this._antragsType;
  }

  public get belege(): Beleg[] {
    return this._belege();
  }

  public set belege(value: Beleg[]) {
    this._belege.set(value);
  }

  public get isComplete(): boolean {
    return this._isCompleted;
  }

  public get timestamp(): string {
    return this._timestamp;
  }

  public set timestamp(value: string) {
    this._timestamp = value;
  }

  public get isLangantrag(): boolean {
    return this.antragsType === AntragsType.LANGANTRAG_LEGACY || this.antragsType === AntragsType.LANGANTRAG;
  }

  public get isFeatureDedicatedBelegEnabledOnAntrag(): boolean {
    return this.firstBelegMandatoryConfig.isActive;
  }

  public get containerId(): string {
    return this._containerId;
  }

  public set containerId(value: string) {
    this._containerId = value;
  }

  public get isPristine(): boolean {
    return this._isPristine;
  }

  public set isPristine(value: boolean) {
    this._isPristine = value;
  }

  public getAntragFinishData(encryptedMetadata: string): AntragFinishDto {
    return new AntragFinishDto(
      this._antragsId.toString(),
      this._belege()
        .filter((b) => b.seiten.length > 0)
        .length.toString(10),
      this.getSeitenLength().toString(10),
      this._antragsType,
      new Date(this._timestamp),
      encryptedMetadata,
      this.getFlags(),
    );
  }

  public async getSizeInBytes(): Promise<number> {
    if (!this.hasBelege() && !this.hasSeiten()) {
      return 0;
    }

    return this._belege().reduce(
      async (size, beleg) => (await size) + (await beleg.getBelegSize()),
      Promise.resolve(0),
    );
  }

  public setComplete(): void {
    this._isCompleted = true;
  }

  public setAntragsType(antragsType: AntragsType): void {
    this._antragsType = antragsType;
  }

  public setAntragsData(antragsData: LegacyAntragsMetadata[] | AntragMetaData[]): void {
    if (antragsData.length === 0) {
      return;
    }
    this._antragsMetadata.length = 0;
    // Type assertion to handle the type mismatch
    this._antragsMetadata.push(...(antragsData as (LegacyAntragsMetadata & AntragMetaData)[]));
  }

  public addBeleg(): Beleg {
    const newBeleg = new Beleg();
    this._belege.set([...this._belege(), newBeleg]);
    return newBeleg;
  }

  public hasAntragsMetadata(): boolean {
    return this._antragsMetadata.length > 0;
  }

  public getSeitenLength(): number {
    return this.getAllSeiten().length;
  }

  public canAddBeleg(): boolean {
    const belegLength = this.belege.length;

    if (belegLength === 0) {
      return true;
    }

    const lastBeleg = this.belege[belegLength - 1];
    return lastBeleg?.seiten.length > 0;
  }

  public hasBelege(): boolean {
    return this._belege()?.length > 0;
  }

  public getAllSeiten(): Seite[] {
    return this._belege()?.reduce((a: Seite[], b: Beleg) => a.concat(b.seiten), []) ?? [];
  }

  public setDataAfterCreationOrReset(antragsId: Guid, timestamp: string): void {
    this._antragsId = antragsId;
    this._timestamp = timestamp;
  }

  public getAntragsMetadata(): LegacyAntragsMetadata[] | AntragMetaData[] {
    return this._antragsMetadata;
  }

  public moveBelegToTop(beleg: Beleg): void {
    const belegIndex = this._belege().indexOf(beleg);
    const topIndex = this.firstBelegMandatoryConfig.isActive ? 1 : 0;

    // only move up if it's not already at the top
    if (belegIndex < topIndex + 1) {
      return;
    }

    const updatedBelege = [...this._belege()];
    updatedBelege.splice(belegIndex, 1);
    updatedBelege.splice(topIndex, 0, beleg);

    this._belege.set(updatedBelege);
  }

  public addOrUpdateSingleMetaDatum(antragsMetaDatum: LegacyAntragsMetadata): void {
    const indexOfMetaDatum = this._antragsMetadata.findIndex(
      (x) => 'keyInternal' in x && x.keyInternal === antragsMetaDatum.keyInternal,
    );
    if (indexOfMetaDatum > -1) {
      this._antragsMetadata.splice(indexOfMetaDatum, 1, antragsMetaDatum);
      return;
    }

    this._antragsMetadata.push(antragsMetaDatum as LegacyAntragsMetadata & AntragMetaData);
  }

  public removeBeleg(beleg: Beleg): void {
    const belegIndexToDelete = this._belege().indexOf(beleg);

    if (belegIndexToDelete !== -1) {
      const updatedBelege = this._belege().filter((b) => b !== beleg);

      this._belege.set(updatedBelege);
    }
  }

  public getFlags(): AntragsFlag[] {
    const flags: AntragsFlag[] = [];

    if (this.firstBelegMandatoryConfig.isActive && this.isLangantrag) {
      flags.push('HasDedicatedFirst');
    }

    return flags;
  }

  public isContentEmpty(): boolean {
    return !this.hasAntragsMetadata() && !this.hasSeiten();
  }

  public canBeDiscarded(): boolean {
    return this.isContentEmpty() && this.isPristine && !this.antragsId;
  }
}

export type AntragsMetadataHelper = Omit<LegacyAntragsMetadata, 'value'> & { valueFromForm: (form: unknown) => string };
